import { useState, useEffect } from "react";
import { Package, Calendar, Mail, DollarSign, Download } from "lucide-react";
import AdminLayout from "@/components/AdminLayout";
import { adminApi, publicApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    totalPackages: 0,
    totalBookings: 0,
    totalInquiries: 0,
    totalRevenue: 0,
  });
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  const handleExport = async (kind) => {
    setDownloading(kind);
    try {
      const res = await adminApi.get(`/admin/export/${kind}.csv`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      link.setAttribute("download", `${kind}_${stamp}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${kind.charAt(0).toUpperCase() + kind.slice(1)} exported`);
    } catch (err) {
      toast.error(`Failed to export ${kind}`);
    } finally {
      setDownloading(null);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [packagesRes, bookingsRes, inquiriesRes] = await Promise.all([
        publicApi.get(`/packages`),
        adminApi.get(`/admin/bookings`),
        adminApi.get(`/admin/inquiries`),
      ]);

      const totalRevenue = bookingsRes.data
        .filter((b) => b.payment_status === "completed")
        .reduce((sum, b) => sum + b.total_amount, 0);

      setStats({
        totalPackages: packagesRes.data.length,
        totalBookings: bookingsRes.data.length,
        totalInquiries: inquiriesRes.data.length,
        totalRevenue,
      });

      setRecentBookings(bookingsRes.data.slice(0, 5));
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: "Total Packages", value: stats.totalPackages, icon: Package, color: "bg-blue-500" },
    { title: "Total Bookings", value: stats.totalBookings, icon: Calendar, color: "bg-green-500" },
    { title: "Inquiries", value: stats.totalInquiries, icon: Mail, color: "bg-orange-500" },
    { title: "Revenue", value: `₹${stats.totalRevenue.toLocaleString('en-IN')}`, icon: DollarSign, color: "bg-purple-500" },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <h1 className="text-3xl font-serif">Dashboard Overview</h1>
          <div className="flex flex-wrap gap-2" data-testid="export-toolbar">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("bookings")}
              disabled={downloading === "bookings"}
              data-testid="export-bookings-btn"
              className="gap-2"
            >
              <Download size={14} />
              {downloading === "bookings" ? "Downloading..." : "Export Bookings"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("users")}
              disabled={downloading === "users"}
              data-testid="export-users-btn"
              className="gap-2"
            >
              <Download size={14} />
              {downloading === "users" ? "Downloading..." : "Export Users"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleExport("revenue")}
              disabled={downloading === "revenue"}
              data-testid="export-revenue-btn"
              className="gap-2"
            >
              <Download size={14} />
              {downloading === "revenue" ? "Downloading..." : "Export Revenue"}
            </Button>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {statCards.map((stat, index) => (
            <div key={index} data-testid={`stat-${stat.title.toLowerCase().replace(' ', '-')}`} className="bg-white rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">{stat.title}</p>
                  <p className="text-2xl font-bold">{stat.value}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg`}>
                  <stat.icon className="w-6 h-6 text-white" />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Recent Bookings */}
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-serif mb-4">Recent Bookings</h2>
          {loading ? (
            <div className="flex justify-center py-8">
              <div className="spinner"></div>
            </div>
          ) : recentBookings.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No bookings yet</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Customer</th>
                    <th className="text-left py-3 px-4">Package</th>
                    <th className="text-left py-3 px-4">Travel Date</th>
                    <th className="text-left py-3 px-4">Amount</th>
                    <th className="text-left py-3 px-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentBookings.map((booking) => (
                    <tr key={booking.id} className="border-b hover:bg-muted/50">
                      <td className="py-3 px-4">{booking.customer_name}</td>
                      <td className="py-3 px-4">{booking.package_name}</td>
                      <td className="py-3 px-4">{booking.travel_date}</td>
                      <td className="py-3 px-4">₹{booking.total_amount.toLocaleString('en-IN')}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          booking.booking_status === "confirmed"
                            ? "bg-green-100 text-green-700"
                            : "bg-orange-100 text-orange-700"
                        }`}>
                          {booking.booking_status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;